/**
 * Read-only notification content pages (metaplan A10; AIE §10 "Read-only views"): what AI editing works on,
 * visible to dashboard users with notification.list. No edit controls: the content is edited through
 * AI editing (MCP) or by Curated Planet in the Ops Console.
 *
 * Routes (NotificationsPage.tsx) are exactly the models' get_dashboard_link() paths:
 *   /notifications/blocks[/:id], /notifications/block_lists[/:id], /notifications/themes[/:id],
 *   /notifications/language_policies[/:id], /notifications/language_aliases[/:id].
 */
import { Alert, Box, Chip, Divider, Grid, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import AccessDeniedView from '../../components/AccessDeniedView';
import PageMeta from '../../components/PageMeta';
import { ContextStore } from '../../store/contextStore';
import { CodeView, ContentDetailLoader, Field, OwnerChips, RowLink } from './common';
import ContentList from './ContentList';

const READ_ONLY_NOTE = 'Read-only: this content is changed through AI editing or by Curated Planet operations.';

const Header = ({ title, subtitle }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
            {subtitle || READ_ONLY_NOTE}
        </Typography>
    </Box>
);

const useCanRead = () => {
    const { hasPerm } = React.useContext(ContextStore);
    return typeof hasPerm === 'function' ? hasPerm('notification', 'list') : false;
};

const RowHeader = ({ row, kind }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
            {kind}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                {row.codename || row.alias}
            </Typography>
            <OwnerChips row={row} />
        </Stack>
        {row.name && <Typography variant="body1">{row.name}</Typography>}
        {row.description && (
            <Typography variant="body2" color="text.secondary">
                {row.description}
            </Typography>
        )}
        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
            {READ_ONLY_NOTE}
        </Alert>
    </Box>
);

// ---- blocks -------------------------------------------------------------------------------------------------

export const BlockDetail = ({ row }) => {
    const overrides = row.channel_overrides && typeof row.channel_overrides === 'object' ? Object.entries(row.channel_overrides) : [];
    return (
        <Box>
            <PageMeta title={`Block - ${row.codename}`} />
            <RowHeader row={row} kind="Notification block" />
            <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6} sm={3}><Field label="Type">{row.type}</Field></Grid>
                <Grid item xs={6} sm={3}><Field label="Template language">{row.template_language}</Field></Grid>
                <Grid item xs={6} sm={3}><Field label="JSON output">{row.is_json ? 'Yes' : 'No'}</Field></Grid>
                <Grid item xs={6} sm={3}><Field label="On error">{row.error_mode}</Field></Grid>
                {row.feature && <Grid item xs={6} sm={3}><Field label="Feature">{row.feature}</Field></Grid>}
            </Grid>
            <CodeView label="Definition" value={row.definition} />
            {row.definition_markdown && <CodeView label="Definition (markdown)" value={row.definition_markdown} />}
            {row.definition_plaintext && <CodeView label="Definition (plain text)" value={row.definition_plaintext} />}
            {overrides.map(([channel, def]) => (
                <CodeView key={channel} label={`Channel override: ${channel}`} value={def} />
            ))}
            {row.default_value && <CodeView label="Default value" value={row.default_value} />}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">Used by</Typography>
            {(row.used_by?.notifications || []).length === 0 && (row.used_by?.block_lists || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">Not referenced by your notifications or block lists.</Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {(row.used_by?.notifications || []).map((n) => (
                    <RowLink key={`n${n.id}`} to={`/notifications/${n.id}`}>
                        <Chip size="small" clickable label={`Notification: ${n.name}`} />
                    </RowLink>
                ))}
                {(row.used_by?.block_lists || []).map((l) => (
                    <RowLink key={`l${l.id}`} to={`/notifications/block_lists/${l.id}`}>
                        <Chip size="small" clickable label={`Block list: ${l.codename}`} />
                    </RowLink>
                ))}
            </Stack>
        </Box>
    );
};

// ---- block lists --------------------------------------------------------------------------------------------

export const BlockListDetail = ({ row }) => (
    <Box>
        <PageMeta title={`Block list - ${row.codename}`} />
        <RowHeader row={row} kind="Block list" />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Items, in order</Typography>
        <Table size="small" aria-label="Block list items">
            <TableHead>
                <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Available as</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {(row.items || []).map((item, idx) => {
                    const ref = item.block || item.block_list;
                    const to = item.block ? `/notifications/blocks/${item.block.id}` : `/notifications/block_lists/${item.block_list?.id}`;
                    return (
                        <TableRow key={`${idx}-${ref?.id}`}>
                            <TableCell>{item.order}</TableCell>
                            <TableCell>
                                {ref ? (
                                    <RowLink to={to}>
                                        <span style={{ fontFamily: 'monospace' }}>{ref.codename}</span>
                                    </RowLink>
                                ) : '—'}{' '}
                                <Typography variant="caption" color="text.secondary">
                                    {item.block ? 'block' : 'block list'}
                                    {ref?.is_system ? ' · system' : ''}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{item.alias || ref?.codename || '—'}</TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </Box>
);

// ---- themes -------------------------------------------------------------------------------------------------

export const ThemeDetail = ({ row }) => (
    <Box>
        <PageMeta title={`Theme - ${row.codename}`} />
        <RowHeader row={row} kind="Notification theme" />
        <Field label="Inline CSS into e-mails">{row.inline_css ? 'Yes' : 'No'}</Field>
        <CodeView label="E-mail shell" value={row.email_shell} />
        <CodeView label="Design tokens" value={row.tokens} emptyText="{}" />
        <Grid container spacing={2}>
            {[
                ['SMS signature', row.sms_signature],
                ['Chat signature', row.chat_signature],
                ['WhatsApp signature', row.whatsapp_signature],
                ['Airbnb signature', row.airbnb_signature],
            ].map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                    <CodeView label={label} value={value} />
                </Grid>
            ))}
        </Grid>
    </Box>
);

// ---- language policies and aliases --------------------------------------------------------------------------

export const LanguagePolicyDetail = ({ row }) => (
    <Box>
        <PageMeta title={`Language policy - ${row.codename}`} />
        <RowHeader row={row} kind="Language policy" />
        {row.is_default && <Chip size="small" color="success" label="Your default policy" sx={{ mb: 1 }} />}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The first rule that yields a language wins; a trailing constant rule is the catch-all default.
        </Typography>
        <Table size="small" aria-label="Language policy rules">
            <TableHead>
                <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Definition</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {(row.rules || []).map((rule) => (
                    <TableRow key={rule.id}>
                        <TableCell>{rule.order}</TableCell>
                        <TableCell>{rule.type}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {rule.block ? (
                                <RowLink to={`/notifications/blocks/${rule.block.id}`}>block {rule.block.codename}</RowLink>
                            ) : rule.nested_policy ? (
                                <RowLink to={`/notifications/language_policies/${rule.nested_policy.id}`}>policy {rule.nested_policy.codename}</RowLink>
                            ) : (
                                rule.definition || '—'
                            )}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </Box>
);

export const LanguageAliasDetail = ({ row }) => (
    <Box>
        <PageMeta title={`Language alias - ${row.alias}`} />
        <RowHeader row={row} kind="Language alias" />
        <Field label="Matches">{row.alias}</Field>
        <Field label="Language">{row.language_code}</Field>
    </Box>
);

// ---- pages --------------------------------------------------------------------------------------------------

const Gate = ({ children }) => (useCanRead() ? children : <AccessDeniedView />);

const makePage = ({ title, subtitle, resource, basePath, kind, columns, Detail }) => {
    const Page = () => {
        const { id } = useParams();
        return (
            <Gate>
                {id ? (
                    <ContentDetailLoader
                        resource={resource}
                        id={id}
                        backTo={basePath}
                        backLabel={title}
                        render={(row) => <Detail row={row} />}
                    />
                ) : (
                    <Box p={2}>
                        <PageMeta title={title} />
                        <Header title={title} subtitle={subtitle} />
                        <ContentList resource={resource} basePath={basePath} columns={columns} ariaLabel={title} />
                    </Box>
                )}
            </Gate>
        );
    };
    Page.displayName = `${kind}Page`;
    return Page;
};

export const BlocksPage = makePage({
    title: 'Blocks',
    subtitle: `Reusable pieces your notifications are composed of. ${READ_ONLY_NOTE}`,
    resource: 'notification_block',
    basePath: '/notifications/blocks',
    kind: 'Blocks',
    columns: [{ label: 'Type', render: (r) => r.type }],
    Detail: BlockDetail,
});

export const BlockListsPage = makePage({
    title: 'Block lists',
    subtitle: `Ordered sets of blocks a notification can include at once. ${READ_ONLY_NOTE}`,
    resource: 'block_list',
    basePath: '/notifications/block_lists',
    kind: 'BlockLists',
    columns: [{ label: 'Items', render: (r) => r.item_count }],
    Detail: BlockListDetail,
});

export const ThemesPage = makePage({
    title: 'Themes',
    subtitle: `The e-mail shell, design tokens and signatures notifications render in. ${READ_ONLY_NOTE}`,
    resource: 'notification_theme',
    basePath: '/notifications/themes',
    kind: 'Themes',
    Detail: ThemeDetail,
});

/** Language policies and aliases on one page, two tabs; `/language_aliases[/:id]` opens the Aliases tab. */
export const LanguagePoliciesPage = ({ aliases = false }) => {
    const { id } = useParams();
    const history = useHistory();
    const tab = aliases ? 1 : 0;
    const resource = aliases ? 'language_alias' : 'language_policy';
    const basePath = aliases ? '/notifications/language_aliases' : '/notifications/language_policies';
    return (
        <Gate>
            {id ? (
                <ContentDetailLoader
                    resource={resource}
                    id={id}
                    backTo={basePath}
                    backLabel={aliases ? 'Language aliases' : 'Language policies'}
                    render={(row) => (aliases ? <LanguageAliasDetail row={row} /> : <LanguagePolicyDetail row={row} />)}
                />
            ) : (
                <Box p={2}>
                    <PageMeta title="Language policies" />
                    <Header
                        title="Language policies"
                        subtitle={`How the language of each notification is chosen, and the names that map to a language. ${READ_ONLY_NOTE}`}
                    />
                    <Tabs
                        value={tab}
                        onChange={(_ev, v) => history.push(v === 1 ? '/notifications/language_aliases' : '/notifications/language_policies')}
                        sx={{ mb: 2 }}
                    >
                        <Tab label="Policies" />
                        <Tab label="Aliases" />
                    </Tabs>
                    {aliases ? (
                        <ContentList
                            key="aliases"
                            resource="language_alias"
                            basePath={basePath}
                            keyLabel="Alias"
                            keyOf={(r) => r.alias}
                            nameOf={(r) => r.language_code}
                            nameLabel="Language"
                            ariaLabel="Language aliases"
                        />
                    ) : (
                        <ContentList
                            key="policies"
                            resource="language_policy"
                            basePath={basePath}
                            ariaLabel="Language policies"
                            columns={[
                                { label: 'Rules', render: (r) => r.rule_count },
                                { label: 'Default', render: (r) => (r.is_default ? 'Yes' : '') },
                            ]}
                        />
                    )}
                </Box>
            )}
        </Gate>
    );
};
